import { useEffect, useRef } from 'react';
import { haptics } from '../../../utils/haptics';
import { stationLabel } from '../../../utils/playlistDelivery';
import { announce as copy } from './playlistCopy';

const MIN_GAP_MS = 1500;

/**
 * Screen-reader announcements for a delivery (spec §5 S5, §6.5): A-6 per
 * sent item, A-6b building, A-7 delivered (polite, at most one per 1.5 s,
 * coalesced to the latest), and A-8 failures (assertive). Also fires the
 * success and error haptics.
 */
export function useDeliveryAnnouncements({ delivery, items, linkedAgents, announce, announceAlert }) {
  const previous = useRef(delivery);
  const lastAt = useRef(0);
  const pending = useRef(null);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    const prev = previous.current;
    previous.current = delivery;
    if (prev === delivery) return;

    const say = (text) => {
      pending.current = text;
      const wait = lastAt.current + MIN_GAP_MS - Date.now();
      if (wait <= 0) {
        lastAt.current = Date.now();
        announce(text);
        pending.current = null;
        return;
      }
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        lastAt.current = Date.now();
        announce(pending.current);
        pending.current = null;
      }, wait);
    };

    const station = stationLabel(linkedAgents, delivery.agentId);
    const finished = prev.status === 'sending' && prev.currentKey && prev.currentKey !== delivery.currentKey
      && delivery.status !== 'failed';
    if (finished) {
      const index = items.findIndex((item) => item.itemKey === prev.currentKey);
      if (index !== -1) say(copy.sent(items[index].title, index + 1, items.length));
    }

    if (delivery.status === prev.status) return;
    if (delivery.status === 'building') say(copy.building(station));
    if (delivery.status === 'done') {
      say(copy.delivered(delivery.groupCount ?? items.length, station));
      haptics.success();
    }
    if (delivery.status === 'failed') {
      clearTimeout(timer.current);
      announceAlert(delivery.errorMessage);
      haptics.error();
    }
  }, [delivery, items, linkedAgents, announce, announceAlert]);
}
